import {
  AlertCircle,
  Bot,
  BotMessageSquare,
  CheckCircle2,
  Download,
  FileCode2,
  GitBranch,
  LoaderCircle,
  RefreshCw,
  ShieldCheck,
  Wrench,
} from "lucide-react";
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@opencloud/ui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AgentApiError, api } from "../api.js";
import type {
  AgentTraceInspectionEntry,
  AgentTraceInspectionEntryKind,
  AgentTraceInspectionView,
  ConversationRun,
} from "../types.js";

export function TraceInspector({
  appId,
  runs,
  open,
  onOpenChange,
}: {
  appId: string;
  runs: readonly ConversationRun[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const rootRuns = useMemo(
    () => runs.filter(({ parentRunId }) => parentRunId === null).reverse(),
    [runs],
  );
  const [runId, setRunId] = useState<string | null>(rootRuns[0]?.id ?? null);
  const [inspection, setInspection] =
    useState<AgentTraceInspectionView | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadVersion = useRef(0);

  useEffect(() => {
    if (!runId || !rootRuns.some(({ id }) => id === runId)) {
      setRunId(rootRuns[0]?.id ?? null);
    }
  }, [rootRuns, runId]);

  const load = useCallback(
    async (offset = 0) => {
      if (!runId) return;
      const version = ++loadVersion.current;
      if (offset === 0) {
        setLoading(true);
        setLoadingMore(false);
      } else {
        setLoadingMore(true);
      }
      try {
        const next = await api<AgentTraceInspectionView>(
          traceInspectionPath(appId, runId, offset),
        );
        if (version !== loadVersion.current) return;
        setInspection((current) =>
          offset === 0 || !current || current.manifest.rootRunId !== runId
            ? next
            : mergeInspectionPages(current, next),
        );
        setError(null);
      } catch (nextError) {
        if (version !== loadVersion.current) return;
        setError(
          nextError instanceof AgentApiError && nextError.status === 404
            ? "Retained evidence is not available for this legacy run."
            : nextError instanceof Error
              ? nextError.message
              : "Run evidence could not be opened.",
        );
      } finally {
        if (version === loadVersion.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [appId, runId],
  );

  useEffect(() => {
    if (!open || !runId) {
      loadVersion.current += 1;
      return;
    }
    setInspection(null);
    setError(null);
    void load();
  }, [load, open, runId]);

  const selectedRun = rootRuns.find(({ id }) => id === runId) ?? null;
  const downloadPath = runId
    ? `/v1/apps/${encodeURIComponent(appId)}/runs/${encodeURIComponent(runId)}/trace`
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="a3-trace-dialog">
        <DialogHeader>
          <p className="a3-eyebrow">Protected run evidence · Read only</p>
          <DialogTitle>Run evidence</DialogTitle>
          <DialogDescription>
            Inspect what the Agent observed and did. Hidden model reasoning and
            credentials are not captured.
          </DialogDescription>
        </DialogHeader>

        {rootRuns.length > 0 ? (
          <div className="a3-trace-toolbar">
            <label>
              <span>Agent run</span>
              <select
                aria-label="Agent run"
                value={runId ?? ""}
                onChange={(event) => setRunId(event.target.value)}
              >
                {rootRuns.map((run) => (
                  <option key={run.id} value={run.id}>
                    {runLabel(run)}
                  </option>
                ))}
              </select>
            </label>
            <Button
              type="button"
              variant="outline"
              onClick={() => void load()}
              disabled={loading || !runId}
            >
              <RefreshCw aria-hidden="true" /> Refresh
            </Button>
            {downloadPath ? (
              <Button
                variant="outline"
                render={<a href={downloadPath} download />}
              >
                <Download aria-hidden="true" /> Download bundle
              </Button>
            ) : null}
          </div>
        ) : null}

        <div className="a3-trace-scroll">
          {loading ? (
            <div className="a3-trace-state" role="status">
              <LoaderCircle className="a3-spin" aria-hidden="true" />
              <p>Opening retained run evidence…</p>
            </div>
          ) : error ? (
            <Alert variant="destructive">
              <AlertCircle aria-hidden="true" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : inspection && selectedRun ? (
            <TraceInspectionContent
              inspection={inspection}
              run={selectedRun}
              loadingMore={loadingMore}
              onLoadMore={() =>
                void load(inspection.window.nextOffset ?? inspection.window.total)
              }
            />
          ) : (
            <div className="a3-trace-state">
              <ShieldCheck aria-hidden="true" />
              <p>No retained run evidence is available yet.</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <p className="a3-trace-private-note">
            The downloaded bundle can contain conversation text and app source.
            Keep it private.
          </p>
          <Button type="button" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TraceInspectionContent({
  inspection,
  run,
  loadingMore,
  onLoadMore,
}: {
  inspection: AgentTraceInspectionView;
  run: ConversationRun;
  loadingMore: boolean;
  onLoadMore: () => void;
}) {
  return (
    <div className="a3-trace-content" data-read-only={inspection.readOnly}>
      <section className="a3-trace-summary" aria-label="Evidence completeness">
        <header>
          <div>
            {inspection.completeness.status === "complete" ? (
              <CheckCircle2 aria-hidden="true" />
            ) : (
              <AlertCircle aria-hidden="true" />
            )}
            <div>
              <h3>{completenessLabel(inspection)}</h3>
              <p>
                {runStatusLabel(run)} · sequence {" "}
                {inspection.object.checkpointedThroughSequence}
              </p>
            </div>
          </div>
          <Badge tone={completenessTone(inspection)}>
            {inspection.object.status}
          </Badge>
        </header>
        {inspection.completeness.reason ? (
          <p>{inspection.completeness.reason}</p>
        ) : null}
        {inspection.completeness.sequenceGaps.length > 0 ||
        inspection.completeness.evidenceGaps.length > 0 ? (
          <Alert variant="destructive" className="a3-trace-gaps">
            <AlertCircle aria-hidden="true" />
            <AlertDescription>
              <strong>Explicit evidence gaps</strong>
              <ul>
                {inspection.completeness.sequenceGaps.map((gap) => (
                  <li key={`${gap.firstSequence}:${gap.lastSequence}`}>
                    {gap.firstSequence}–{gap.lastSequence}: {gap.reason}
                  </li>
                ))}
                {inspection.completeness.evidenceGaps.map((gap) => (
                  <li key={`${gap.category}:${gap.code}`}>
                    {humanize(gap.category)}: {gap.detail}
                  </li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        ) : null}
        <ul className="a3-trace-categories" aria-label="Captured evidence categories">
          {inspection.completeness.categories.map((category) => (
            <li key={category.category} data-status={category.status}>
              <span>{humanize(category.category)}</span>
              <strong>{humanize(category.status)}</strong>
              <small>
                {category.retainedCount} retained / {category.observedCount} observed
              </small>
              {category.reason ? <small>{category.reason}</small> : null}
            </li>
          ))}
        </ul>
      </section>

      <section className="a3-trace-provenance" aria-label="Pinned run inputs">
        <div>
          <h3>Pinned run inputs</h3>
          <p>
            {inspection.manifest.runtime.adapter} · {inspection.manifest.runtime.model ?? "default model"}
          </p>
        </div>
        {inspection.inputs.length > 0 ? (
          <ul>
            {inspection.inputs.map((input) => (
              <li key={`${input.kind}:${input.name}:${input.digest}`}>
                <strong>{input.name}</strong>
                <span>{input.version ?? humanize(input.kind)}</span>
                <code>{shortDigest(input.digest)}</code>
              </li>
            ))}
          </ul>
        ) : (
          <p>No additional pinned-input declarations were retained.</p>
        )}
        <details>
          <summary>Initial command</summary>
          <pre>{inspection.manifest.initialCommand}</pre>
          {inspection.manifest.initialCommandTruncated ? (
            <p>Shown content is bounded. Download the bundle for the complete retained value.</p>
          ) : null}
        </details>
      </section>

      {inspection.usage.reports > 0 ? (
        <section className="a3-trace-usage" aria-label="Provider usage">
          <h3>Provider usage</h3>
          <dl>
            <div><dt>Input</dt><dd>{formatNumber(inspection.usage.inputTokens)}</dd></div>
            <div><dt>Cached input</dt><dd>{formatNumber(inspection.usage.cachedInputTokens)}</dd></div>
            <div><dt>Output</dt><dd>{formatNumber(inspection.usage.outputTokens)}</dd></div>
            <div><dt>Reasoning</dt><dd>{formatNumber(inspection.usage.reasoningTokens)}</dd></div>
            <div><dt>Total</dt><dd>{formatNumber(inspection.usage.totalTokens)}</dd></div>
          </dl>
        </section>
      ) : null}

      <section className="a3-trace-timeline" aria-label="Root and child activity lanes">
        <header>
          <div>
            <GitBranch aria-hidden="true" />
            <div>
              <h3>Activity lanes</h3>
              <p>
                {inspection.window.total} retained entries across {inspection.lanes.length} {inspection.lanes.length === 1 ? "lane" : "lanes"}
              </p>
            </div>
          </div>
        </header>
        <div className="a3-trace-lanes">
          {inspection.lanes.map((lane) => {
            const entries = inspection.entries.filter(
              ({ runId }) => runId === lane.runId,
            );
            return (
              <article key={lane.runId} data-kind={lane.kind}>
                <header>
                  {lane.kind === "root" ? <BotMessageSquare aria-hidden="true" /> : <Bot aria-hidden="true" />}
                  <div>
                    <h4>{lane.label}</h4>
                    <p>{lane.eventCount} retained entries</p>
                  </div>
                </header>
                {entries.length > 0 ? (
                  <ol>
                    {entries.map((entry) => (
                      <TraceEntry key={entry.id} entry={entry} />
                    ))}
                  </ol>
                ) : (
                  <p className="a3-trace-lane-empty">
                    No entries from this lane are in the loaded window.
                  </p>
                )}
              </article>
            );
          })}
        </div>
        {inspection.window.nextOffset !== null ? (
          <Button
            type="button"
            variant="outline"
            onClick={onLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? <LoaderCircle className="a3-spin" aria-hidden="true" /> : null}
            {loadingMore ? "Loading…" : "Load more evidence"}
          </Button>
        ) : null}
      </section>
    </div>
  );
}

function TraceEntry({ entry }: { entry: AgentTraceInspectionEntry }) {
  const Icon = entryIcon(entry.kind);
  return (
    <li data-kind={entry.kind} data-sequence={entry.firstSequence}>
      <details>
        <summary>
          <Icon aria-hidden="true" />
          <span>
            <strong>{entry.title}</strong>
            <small>
              {entry.summary ? `${entry.summary} · ` : ""}
              {timeLabel(entry.occurredAt)} · #{sequenceLabel(entry)}
            </small>
          </span>
        </summary>
        {entry.detail ? <pre>{entry.detail}</pre> : null}
        {entry.detailTruncated ? (
          <p>Shown content is bounded. The protected download retains the complete value.</p>
        ) : null}
        <dl>
          {entry.toolCallId ? <><dt>Tool call</dt><dd><code>{entry.toolCallId}</code></dd></> : null}
          {entry.spanId ? <><dt>Span</dt><dd><code>{entry.spanId}</code></dd></> : null}
        </dl>
      </details>
    </li>
  );
}

export function mergeInspectionPages(
  current: AgentTraceInspectionView,
  next: AgentTraceInspectionView,
): AgentTraceInspectionView {
  if (current.manifest.rootRunId !== next.manifest.rootRunId) return next;
  const entries = new Map(
    [...current.entries, ...next.entries].map((entry) => [entry.id, entry]),
  );
  return { ...next, entries: [...entries.values()] };
}

export function traceInspectionPath(
  appId: string,
  rootRunId: string,
  offset: number,
  limit = 50,
  laneRunId?: string,
): string {
  const lane = laneRunId
    ? `&laneRunId=${encodeURIComponent(laneRunId)}`
    : "";
  return `/v1/apps/${encodeURIComponent(appId)}/runs/${encodeURIComponent(rootRunId)}/trace/inspection?offset=${offset}&limit=${limit}${lane}`;
}

function runLabel(run: ConversationRun): string {
  const created = new Date(run.createdAt);
  const date = Number.isNaN(created.getTime())
    ? "Agent run"
    : new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(created);
  return `${date} · ${humanize(run.status)}`;
}

function runStatusLabel(run: ConversationRun): string {
  return `${humanize(run.status)} run`;
}

function completenessLabel(inspection: AgentTraceInspectionView): string {
  if (inspection.completeness.status === "complete") return "Evidence complete";
  if (inspection.completeness.status === "collecting") return "Evidence collecting";
  return "Evidence incomplete";
}

function completenessTone(
  inspection: AgentTraceInspectionView,
): "success" | "warning" | "danger" | "neutral" {
  if (inspection.object.status === "corrupt") return "danger";
  if (inspection.completeness.status === "complete") return "success";
  if (inspection.completeness.status === "collecting") return "neutral";
  return "warning";
}

function entryIcon(kind: AgentTraceInspectionEntryKind) {
  if (kind === "tool") return Wrench;
  if (kind === "workspace" || kind === "artifact") return FileCode2;
  if (kind === "control") return ShieldCheck;
  if (kind === "child") return Bot;
  if (kind === "lifecycle") return RefreshCw;
  return BotMessageSquare;
}

function humanize(value: string): string {
  return value
    .replace(/[._/-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function sequenceLabel(entry: AgentTraceInspectionEntry): string {
  return entry.firstSequence === entry.lastSequence
    ? String(entry.firstSequence)
    : `${entry.firstSequence}–${entry.lastSequence}`;
}

function timeLabel(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat(undefined, { timeStyle: "medium" }).format(date);
}

function shortDigest(value: string): string {
  return value.length > 24 ? `${value.slice(0, 21)}…` : value;
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat().format(value);
}
